/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect } from 'react';
import type { Product } from '@shared/types/products';
import { Save, Github, GitMerge, Loader, FolderOpen, HardDrive, Info } from 'lucide-react';
import { GitHubService, GitHubRepository } from '../../services/githubService';
import { GitLabService, GitLabProject } from '../../services/gitlabService';
import { GitLabRepoSelector } from './GitLabRepoSelector';
import type { SourceControlProvider } from '../../types';

export type ProductFormData = {
  id?: Product['id'];
  productId?: Product['productId'];
  title: Product['title'];
  description: Product['description'];
  owner: Product['owner'];
  links: NonNullable<Product['links']>;
  capabilities: NonNullable<Product['capabilities']>;
  users: NonNullable<Product['users']>;
  repositoryUrl?: Product['repositoryUrl'] | null;
  repoPath?: Product['repoPath'] | null;
};

/**
 * Repository mode for product creation:
 * - 'link-remote': Link to an existing remote repository (clone it)
 * - 'local-only': Create a local-only repository (no remote)
 * 
 * Note: Every product MUST have a local repo. The choice is whether to link to a remote.
 */
type RepoMode = 'link-remote' | 'local-only';

interface SubmitOptions {
  repoMode: RepoMode;
  // Parsed array fields to ensure submit uses latest values
  parsedArrays?: {
    capabilities: string[];
    users: string[];
    links: string[];
  };
}

interface ProductFormProps {
  formData: ProductFormData;
  onFormDataChange: (data: ProductFormData) => void;
  onSubmit: (options?: SubmitOptions) => void;
  onCancel: () => void;
  isEditing: boolean;
  githubService: GitHubService | null;
  isGitHubConfigured: boolean;
  gitlabService?: GitLabService | null;
  isGitLabConfigured?: boolean;
  sourceControlProvider?: SourceControlProvider;
  isCreatingRepo?: boolean;
  createRepoErrorMessage?: string | null;
}

// Export RepoMode for parent components
export type { RepoMode };

export function ProductForm({
  formData,
  onFormDataChange,
  onSubmit,
  onCancel,
  isEditing,
  githubService,
  isGitHubConfigured,
  gitlabService,
  isGitLabConfigured,
  sourceControlProvider = 'github',
  isCreatingRepo = false,
  createRepoErrorMessage = null,
}: ProductFormProps) {
  const [repositories, setRepositories] = useState<(GitHubRepository | GitLabProject)[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoError, setRepoError] = useState<string | null>(null);
  
  // Local state for array fields - allows typing without immediate parsing
  // This prevents the issue where typing a comma would immediately remove it
  const [capabilitiesText, setCapabilitiesText] = useState(() => formData.capabilities.join(', '));
  const [usersText, setUsersText] = useState(() => formData.users.join(', '));
  const [linksText, setLinksText] = useState(() => formData.links.join(', '));
  
  // Sync local text state when formData changes from outside (e.g., editing a different product)
  useEffect(() => {
    setCapabilitiesText(formData.capabilities.join(', '));
    setUsersText(formData.users.join(', '));
    setLinksText(formData.links.join(', '));
  }, [formData.id]); // Re-sync when switching products (id changes)
  
  // Check if source control is configured
  const isSourceControlConfigured = sourceControlProvider === 'github' 
    ? isGitHubConfigured 
    : isGitLabConfigured;
  
  // Repository mode: link to remote or local-only
  // Default to link-remote if editing with existing repo, otherwise local-only
  const [repoMode, setRepoMode] = useState<RepoMode>(() => {
    if (formData.repositoryUrl) return 'link-remote';
    return 'local-only';
  });
  
  // Use external error message if provided
  const createRepoError = createRepoErrorMessage;

  // Load repositories when form opens and source control is configured
  useEffect(() => {
    const loadRepositories = async () => {
      if (sourceControlProvider === 'github') {
        if (!githubService || !isGitHubConfigured) {
          setRepositories([]);
          return;
        }

        setLoadingRepos(true);
        setRepoError(null);
        try {
          const repos = await githubService.getRepositories();
          setRepositories(repos);
        } catch (error) {
          console.error('Failed to load repositories:', error);
          setRepoError('Failed to load repositories. Please check your GitHub token in Settings.');
        } finally {
          setLoadingRepos(false);
        }
      } else if (sourceControlProvider === 'gitlab') {
        if (!gitlabService || !isGitLabConfigured) {
          setRepositories([]);
          return;
        }

        setLoadingRepos(true);
        setRepoError(null);
        try {
          const projects = await gitlabService.getProjects();
          setRepositories(projects);
        } catch (error) {
          console.error('Failed to load projects:', error);
          setRepoError('Failed to load projects. Please check your GitLab token in Settings.');
        } finally {
          setLoadingRepos(false);
        }
      }
    };

    loadRepositories();
  }, [githubService, isGitHubConfigured, gitlabService, isGitLabConfigured, sourceControlProvider]);

  // Parse comma-separated text into array, filtering empty strings
  const parseArrayField = (value: string): string[] => {
    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  // Update formData when user leaves the field (blur)
  const handleArrayFieldBlur = (field: 'capabilities' | 'users' | 'links') => {
    const textValue = field === 'capabilities' ? capabilitiesText 
                    : field === 'users' ? usersText 
                    : linksText;
    const items = parseArrayField(textValue);
    onFormDataChange({ ...formData, [field]: items });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If linking to remote, ensure a repository is selected
    if (repoMode === 'link-remote' && !formData.repositoryUrl) {
      return; // Don't submit - repository must be selected
    }
    
    // Parse array fields from current text state and pass to parent
    // This ensures submit uses the latest values even if blur hasn't fired
    const parsedArrays = {
      capabilities: parseArrayField(capabilitiesText),
      users: parseArrayField(usersText),
      links: parseArrayField(linksText),
    };
    
    // Pass the repo mode and parsed arrays so the parent uses correct values
    onSubmit({ repoMode, parsedArrays });
  };

  return (
    <div style={{ 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      padding: '24px', 
      marginBottom: '24px',
      background: '#f9f9f9'
    }}>
      <h3 style={{ marginBottom: '20px' }}>
        {isEditing ? 'Edit Product' : 'Create New Product'}
      </h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={formData.title || ''}
            onChange={(e) => onFormDataChange({ ...formData, title: e.target.value })}
            placeholder="Product name"
            required
          />
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
            placeholder="Detailed product description"
            rows={4}
            required
          />
        </div>

        <div className="form-group">
          <label>Owner *</label>
          <input
            type="text"
            value={formData.owner || ''}
            onChange={(e) => onFormDataChange({ ...formData, owner: e.target.value })}
            placeholder="Person or organization name"
            required
          />
        </div>

        <div className="form-group">
          <label>Repository Path</label>
          <input
            type="text"
            value={formData.repoPath ?? ''}
            onChange={(e) =>
              onFormDataChange({
                ...formData,
                repoPath: e.target.value.trim().length > 0 ? e.target.value.trim() : null,
              })
            }
            placeholder="Relative path within the repository (e.g., product)"
          />
          <small style={{ color: '#666', fontSize: '12px' }}>
            Leave blank if the product lives at the repository root.
          </small>
        </div>

        <div className="form-group">
          <label>Capabilities</label>
          <input
            type="text"
            value={capabilitiesText}
            onChange={(e) => setCapabilitiesText(e.target.value)}
            onBlur={() => handleArrayFieldBlur('capabilities')}
            placeholder="Feature categories (comma-separated)"
          />
          <small style={{ color: '#666', fontSize: '12px' }}>
            Example: Products, Issues, User Stories, Releases
          </small>
        </div>

        <div className="form-group">
          <label>User Roles</label>
          <input
            type="text"
            value={usersText}
            onChange={(e) => setUsersText(e.target.value)}
            onBlur={() => handleArrayFieldBlur('users')}
            placeholder="User roles (comma-separated)"
          />
          <small style={{ color: '#666', fontSize: '12px' }}>
            Example: User, Admin, Product Manager, Developer
          </small>
        </div>

        <div className="form-group">
          <label>Links</label>
          <textarea
            value={linksText}
            onChange={(e) => setLinksText(e.target.value)}
            onBlur={() => handleArrayFieldBlur('links')}
            placeholder="URLs or file paths (comma-separated)"
            rows={3}
          />
          <small style={{ color: '#666', fontSize: '12px' }}>
            Example: http://localhost:3001, https://github.com/org/repo
          </small>
        </div>

        <div className="form-group" style={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: '6px', 
          padding: '16px',
          background: '#fff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <HardDrive size={18} style={{ marginRight: '8px', color: '#0066cc' }} />
            <label style={{ margin: 0 }}>Repository</label>
          </div>

          {/* Info banner about local repo requirement */}
          <div style={{ 
            padding: '10px 12px', 
            background: '#e7f3ff', 
            border: '1px solid #0066cc',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#004080',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <Info size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>
              Every product requires a local repository for issue tracking, user stories, and code management.
              {isSourceControlConfigured && (
                <> You can optionally link to an existing {sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} repository.</>
              )}
            </span>
          </div>
          
          {/* Repository Mode Toggle - only show if source control is configured */}
          {isSourceControlConfigured && (
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginBottom: '16px',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                onClick={() => {
                  setRepoMode('local-only');
                  onFormDataChange({ ...formData, repositoryUrl: null });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  border: repoMode === 'local-only' ? '2px solid #0066cc' : '1px solid #ddd',
                  borderRadius: '6px',
                  background: repoMode === 'local-only' ? '#e7f3ff' : '#fff',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: repoMode === 'local-only' ? 600 : 400,
                  color: repoMode === 'local-only' ? '#0066cc' : '#333',
                }}
              >
                <HardDrive size={14} />
                Local Only
              </button>
              <button
                type="button"
                onClick={() => {
                  setRepoMode('link-remote');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  border: repoMode === 'link-remote' ? '2px solid #0066cc' : '1px solid #ddd',
                  borderRadius: '6px',
                  background: repoMode === 'link-remote' ? '#e7f3ff' : '#fff',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: repoMode === 'link-remote' ? 600 : 400,
                  color: repoMode === 'link-remote' ? '#0066cc' : '#333',
                }}
              >
                {sourceControlProvider === 'github' ? <Github size={14} /> : <GitMerge size={14} />}
                Link to {sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'}
              </button>
            </div>
          )}

          {/* Link to Remote Repository */}
          {isSourceControlConfigured && repoMode === 'link-remote' && (
            <>
              {loadingRepos ? (
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px', color: '#666' }}>
                  <Loader size={16} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                  Loading repositories...
                </div>
              ) : repoError ? (
                <div style={{ 
                  padding: '12px', 
                  background: '#f8d7da', 
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#721c24'
                }}>
                  {repoError}
                </div>
              ) : repositories.length > 0 ? (
                sourceControlProvider === 'gitlab' ? (
                  <>
                    <GitLabRepoSelector
                      projects={repositories as GitLabProject[]}
                      selectedRepoUrl={formData.repositoryUrl ?? ''}
                      onSelectedRepoUrlChange={(url) =>
                        onFormDataChange({
                          ...formData,
                          repositoryUrl: url || null,
                        })
                      }
                    />
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                      The selected repository will be cloned locally for development.
                    </small>
                  </>
                ) : (
                  <>
                    <select
                      value={formData.repositoryUrl ?? ''}
                      onChange={(e) =>
                        onFormDataChange({
                          ...formData,
                          repositoryUrl: e.target.value ? e.target.value : null,
                        })
                      }
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select a repository...</option>
                      {(repositories as GitHubRepository[]).map(repo => (
                        <option key={repo.full_name} value={repo.html_url}>
                          {repo.full_name} ({repo.private ? 'Private' : 'Public'})
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                      The selected repository will be cloned locally for development.
                    </small>
                  </>
                )
              ) : (
                <div style={{ 
                  padding: '12px', 
                  background: '#fff3cd', 
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}>
                  No repositories found. Make sure your {sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} token has access to at least one repository.
                </div>
              )}
            </>
          )}

          {/* Local Only Mode */}
          {(repoMode === 'local-only' || !isSourceControlConfigured) && (
            <div style={{ 
              padding: '12px', 
              background: '#f8f9fa', 
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#495057'
            }}>
              <strong>Local Repository</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                A local repository will be initialized for this product. You can push it to {sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} later from the product settings.
              </p>
            </div>
          )}

          {createRepoError && (
            <div style={{ 
              marginTop: '12px',
              padding: '10px 12px', 
              background: '#f8d7da', 
              border: '1px solid #dc3545',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#721c24'
            }}>
              {createRepoError}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          <button 
            type="submit" 
            className="btn" 
            style={{ flex: 1 }}
            disabled={isCreatingRepo || (repoMode === 'link-remote' && !formData.repositoryUrl)}
          >
            {isCreatingRepo ? (
              <>
                <Loader size={16} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                {repoMode === 'link-remote' ? 'Cloning Repository...' : 'Initializing Repository...'}
              </>
            ) : (
              <>
                <Save size={16} style={{ marginRight: '8px' }} />
                {isEditing ? 'Update Product' : 'Create Product'}
              </>
            )}
          </button>
          <button 
            type="button" 
            onClick={onCancel} 
            className="btn" 
            style={{ background: '#f5f5f5', color: '#333' }}
            disabled={isCreatingRepo}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

